import { ChevronDown, FileSpreadsheet, UploadCloud, X, ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import { getConfigWithToken } from "../config/config";
import { getValidationImportRun, previewValidationCsv, startValidationImport } from "../config/url";
import {
	clearActiveImportRun,
	isImportRunFinished,
	requestImportNotificationPermission,
	saveActiveImportRun,
} from "../utils/ehrImportRun";
import {
	EHR_MAPPING_FIELDS,
	loadSavedColumnMapping,
	mergeSavedMapping,
	parseCsvPreview,
	saveColumnMapping,
	suggestColumnMapping,
	validateColumnMapping,
} from "../utils/ehrCsvMapping";
import { formatFileSize, validateEhrCsvFile } from "../utils/ehrCsvValidation";
import EhrCsvReviewStep from "./EhrCsvReviewStep";
import EhrCsvProcessingStep from "./EhrCsvProcessingStep";
import classes from "./EhrCsvUploadModal.module.css";

const UPLOAD_STATUS = {
	IDLE: "idle",
	READY: "Ready",
	INVALID: "Invalid file",
	UPLOADING: "Uploading...",
	UPLOADED: "Uploaded",
	FAILED: "Upload failed",
};

const STEPS = {
	UPLOAD: 1,
	MAP: 2,
	REVIEW: 3,
	PROCESSING: 4,
};

const buildInitialMapping = () =>
	EHR_MAPPING_FIELDS.reduce((accumulator, field) => {
		accumulator[field.key] = "";
		return accumulator;
	}, {});

const EhrCsvUploadModal = ({
	initialImportRun = null,
	isOpen,
	onClose,
	onUploadSuccess,
	organizationId,
	token,
	uploadedByName,
}) => {
	const fileInputRef = useRef(null);
	const [step, setStep] = useState(STEPS.UPLOAD);
	const [selectedFile, setSelectedFile] = useState(null);
	const [fileContent, setFileContent] = useState("");
	const [rowCount, setRowCount] = useState(null);
	const [csvHeaders, setCsvHeaders] = useState([]);
	const [sampleValues, setSampleValues] = useState([]);
	const [columnMapping, setColumnMapping] = useState(buildInitialMapping);
	const [importPreview, setImportPreview] = useState(null);
	const [importRun, setImportRun] = useState(null);
	const [validationError, setValidationError] = useState("");
	const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.IDLE);
	const [isDragActive, setIsDragActive] = useState(false);
	const [isValidating, setIsValidating] = useState(false);
	const [isPreviewLoading, setIsPreviewLoading] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [saveMapping, setSaveMapping] = useState(true);

	const resetState = useCallback(() => {
		setStep(STEPS.UPLOAD);
		setSelectedFile(null);
		setFileContent("");
		setRowCount(null);
		setCsvHeaders([]);
		setSampleValues([]);
		setColumnMapping(buildInitialMapping());
		setImportPreview(null);
		setImportRun(null);
		setValidationError("");
		setUploadStatus(UPLOAD_STATUS.IDLE);
		setIsDragActive(false);
		setIsValidating(false);
		setIsPreviewLoading(false);
		setIsUploading(false);
		setSaveMapping(true);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	}, []);

	const buildRequestHeaders = useCallback(
		() => ({
			...(token ? getConfigWithToken(token).headers : {}),
			...(organizationId ? { "x-organization-id": organizationId } : {}),
		}),
		[organizationId, token]
	);

	useEffect(() => {
		if (!isOpen || !initialImportRun) return;
		setImportRun(initialImportRun);
		setStep(STEPS.PROCESSING);
	}, [initialImportRun, isOpen]);

	const fetchImportRunStatus = useCallback(
		async (runId) => {
			if (!runId || !organizationId) return null;

			const response = await axios.get(getValidationImportRun(runId), {
				headers: buildRequestHeaders(),
				params: organizationId ? { organizationId } : undefined,
			});

			return response?.data?.data || null;
		},
		[buildRequestHeaders, organizationId]
	);

	const handleImportRunFinished = useCallback(
		async (run) => {
			clearActiveImportRun(organizationId);

			if (run?.status === "completed") {
				await onUploadSuccess?.(run);
				onClose();
				return;
			}

			if (run?.status === "failed") {
				toast.error(run.errorMessage || "EHR import failed.");
			}
		},
		[onClose, onUploadSuccess, organizationId]
	);

	useEffect(() => {
		if (!isOpen || step !== STEPS.PROCESSING || !importRun?.runId) return undefined;

		let cancelled = false;

		const poll = async () => {
			try {
				const latest = await fetchImportRunStatus(importRun.runId);
				if (cancelled || !latest) return;

				setImportRun(latest);

				if (isImportRunFinished(latest.status)) {
					await handleImportRunFinished(latest);
				}
			} catch {
				if (!cancelled) {
					toast.error("Unable to load import progress.");
				}
			}
		};

		poll();
		const intervalId = window.setInterval(poll, 2000);

		return () => {
			cancelled = true;
			window.clearInterval(intervalId);
		};
	}, [fetchImportRunStatus, handleImportRunFinished, importRun?.runId, isOpen, step]);

	useEffect(() => {
		if (!isOpen) {
			resetState();
			return undefined;
		}

		const onKeyDown = (event) => {
			if (event.key === "Escape" && !isUploading && !isPreviewLoading) {
				onClose();
			}
		};

		document.addEventListener("keydown", onKeyDown);
		document.body.style.overflow = "hidden";

		return () => {
			document.removeEventListener("keydown", onKeyDown);
			document.body.style.overflow = "";
		};
	}, [isOpen, isPreviewLoading, isUploading, onClose, resetState]);

	const mappingValidation = useMemo(
		() => validateColumnMapping(columnMapping, csvHeaders),
		[columnMapping, csvHeaders]
	);

	const usedColumns = useMemo(() => {
		const used = new Set();
		Object.values(columnMapping).forEach((value) => {
			const trimmed = String(value || "").trim();
			if (trimmed) used.add(trimmed);
		});
		return used;
	}, [columnMapping]);

	const processFile = useCallback(async (file) => {
		if (!file) return;

		setIsValidating(true);
		setValidationError("");
		setUploadStatus(UPLOAD_STATUS.IDLE);
		setRowCount(null);
		setFileContent("");
		setImportPreview(null);
		setStep(STEPS.UPLOAD);
		setColumnMapping(buildInitialMapping());

		const result = await validateEhrCsvFile(file);

		if (!result.isValid) {
			setSelectedFile(file);
			setValidationError(result.error || "The selected file is invalid.");
			setUploadStatus(UPLOAD_STATUS.INVALID);
			setIsValidating(false);
			return;
		}

		setSelectedFile(file);
		setFileContent(result.fileContent || "");
		setRowCount(result.rowCount);
		setUploadStatus(result.status || UPLOAD_STATUS.READY);
		setIsValidating(false);
	}, []);

	const initializeMappingStep = useCallback(() => {
		const preview = parseCsvPreview(fileContent);
		const savedMapping = loadSavedColumnMapping(organizationId);
		const mergedMapping = mergeSavedMapping(savedMapping, preview.headers);
		const suggestedMapping = suggestColumnMapping(preview.headers);

		const nextMapping = buildInitialMapping();
		EHR_MAPPING_FIELDS.forEach(({ key }) => {
			nextMapping[key] = mergedMapping[key] || suggestedMapping[key] || "";
		});

		setCsvHeaders(preview.headers);
		setSampleValues(preview.sampleValues);
		setColumnMapping(nextMapping);
		setImportPreview(null);
		setStep(STEPS.MAP);
	}, [fileContent, organizationId]);

	const loadImportPreview = useCallback(async () => {
		if (!selectedFile || !mappingValidation.isValid) return false;

		if (!organizationId) {
			toast.error("Organization context is required.");
			return false;
		}

		const formData = new FormData();
		formData.append("file", selectedFile);
		formData.append("columnMapping", JSON.stringify(columnMapping));

		setIsPreviewLoading(true);

		try {
			const response = await axios.post(previewValidationCsv, formData, {
				headers: {
					...buildRequestHeaders(),
					"Content-Type": "multipart/form-data",
				},
				params: organizationId ? { organizationId } : undefined,
			});

			const previewData = response?.data?.data || null;
			setImportPreview(
				previewData
					? {
							...previewData,
							fileSummary: {
								...previewData.fileSummary,
								previewedAt: previewData.fileSummary?.previewedAt || new Date().toISOString(),
							},
						}
					: null,
			);
			setStep(STEPS.REVIEW);
			return true;
		} catch (error) {
			toast.error(error?.response?.data?.message || "Import preview could not be generated.");
			return false;
		} finally {
			setIsPreviewLoading(false);
		}
	}, [
		buildRequestHeaders,
		columnMapping,
		mappingValidation.isValid,
		organizationId,
		selectedFile,
	]);

	const handleBrowseClick = () => {
		if (isUploading || isValidating || isPreviewLoading) return;
		fileInputRef.current?.click();
	};

	const handleInputChange = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		await processFile(file);
	};

	const handleDragEnter = (event) => {
		event.preventDefault();
		event.stopPropagation();
		if (isUploading || isValidating || isPreviewLoading) return;
		setIsDragActive(true);
	};

	const handleDragOver = (event) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const handleDragLeave = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setIsDragActive(false);
	};

	const handleDrop = async (event) => {
		event.preventDefault();
		event.stopPropagation();
		setIsDragActive(false);
		if (isUploading || isValidating || isPreviewLoading) return;

		const file = event.dataTransfer?.files?.[0];
		await processFile(file);
	};

	const handleRemoveFile = () => {
		if (isUploading || isPreviewLoading) return;
		resetState();
	};


	const handleBack = () => {
		if (isUploading || isPreviewLoading) return;
		if (step === STEPS.REVIEW) {
			setStep(STEPS.MAP);
			return;
		}
		if (step === STEPS.MAP) {
			setStep(STEPS.UPLOAD);
		}
	};

	const handleMappingChange = (fieldKey, value) => {
		setColumnMapping((previous) => {
			const next = { ...previous, [fieldKey]: value };

			if (value) {
				EHR_MAPPING_FIELDS.forEach(({ key }) => {
					if (key !== fieldKey && next[key] === value) {
						next[key] = "";
					}
				});
			}

			return next;
		});
		setImportPreview(null);
	};

	const handleNext = () => {
		if (!selectedFile || !rowCount || validationError || isValidating || isUploading || isPreviewLoading) return;

		try {
			initializeMappingStep();
		} catch (error) {
			toast.error(error.message || "The CSV could not be prepared for column mapping.");
		}
	};

	const handleContinueToReview = async () => {
		if (!mappingValidation.isValid || isPreviewLoading || isUploading) return;

		if (saveMapping) {
			saveColumnMapping(organizationId, columnMapping);
		}

		await loadImportPreview();
	};

	const handleImportAndValidate = async () => {
		if (!selectedFile || !importPreview?.importableRowNumbers?.length || isUploading) return;

		if (!organizationId) {
			toast.error("Organization context is required.");
			return;
		}

		await requestImportNotificationPermission();

		const formData = new FormData();
		formData.append("file", selectedFile);
		formData.append("columnMapping", JSON.stringify(columnMapping));
		formData.append("importRowNumbers", JSON.stringify(importPreview.importableRowNumbers));
		formData.append(
			"previewCounts",
			JSON.stringify({
				excludedRows:
					(importPreview.counts?.invalidRows || 0) +
					(importPreview.counts?.previouslyImported || 0) +
					(importPreview.counts?.mappingRequired || 0),
				invalidRows: importPreview.counts?.invalidRows || 0,
				mappingRequired: importPreview.counts?.mappingRequired || 0,
				previouslyImported: importPreview.counts?.previouslyImported || 0,
				readyToImport: importPreview.counts?.readyToImport || 0,
				totalRows: importPreview.counts?.totalRows || 0,
				warnings: importPreview.counts?.warnings || 0,
			})
		);

		setIsUploading(true);
		setUploadStatus(UPLOAD_STATUS.UPLOADING);

		try {
			const response = await axios.post(startValidationImport, formData, {
				headers: {
					...buildRequestHeaders(),
					"Content-Type": "multipart/form-data",
				},
				params: organizationId ? { organizationId } : undefined,
			});

			const run = response?.data?.data;
			if (!run?.runId) {
				throw new Error("Import run could not be started.");
			}

			saveActiveImportRun(organizationId, run.runId);
			setImportRun(run);
			setStep(STEPS.PROCESSING);
			setUploadStatus(UPLOAD_STATUS.UPLOADED);
		} catch (error) {
			setUploadStatus(UPLOAD_STATUS.FAILED);
			toast.error(error?.response?.data?.message || "EHR import could not be started.");
		} finally {
			setIsUploading(false);
		}
	};

	const handleCloseModal = () => {
		if (step === STEPS.PROCESSING && importRun && !isImportRunFinished(importRun.status)) {
			onClose();
			return;
		}
		onClose();
	};

	const isProcessingStep = step === STEPS.PROCESSING;

	const hasValidFile = Boolean(selectedFile && rowCount > 0 && !validationError);
	const canProceedToMapping = hasValidFile && !isValidating && !isUploading && !isPreviewLoading;
	const canContinueToReview = mappingValidation.isValid && !isPreviewLoading && !isUploading;
	const getUploadStatusClass = (status) => {
		if (status === UPLOAD_STATUS.INVALID || status === UPLOAD_STATUS.FAILED) {
			return classes.fileStatusError;
		}
		if (status === UPLOAD_STATUS.UPLOADING) {
			return classes.fileStatusPending;
		}
		return classes.fileStatus;
	};
	const canImportAndValidate =
		Boolean(importPreview?.counts?.readyToImport > 0) && !isUploading && !isPreviewLoading;

	const modalClassName = isProcessingStep
		? `${classes.modal} ${classes.modalProcessing}`
		: step === STEPS.REVIEW
			? `${classes.modal} ${classes.modalReview}`
			: classes.modal;

	const stepTitle = isProcessingStep
		? "Process Import"
		: step === STEPS.REVIEW
			? "Review & Run Validation"
			: step === STEPS.MAP
				? "Map CSV Columns"
				: "Upload EHR File";

	if (!isOpen) return null;

	return (
		<div
			className={classes.overlay}
			onClick={handleCloseModal}
			onKeyDown={(event) => {
				if (event.key === "Escape") handleCloseModal();
			}}
			role="presentation">
			<div
				aria-labelledby="ehr-upload-title"
				aria-modal="true"
				className={modalClassName}
				onClick={(event) => event.stopPropagation()}
				role="dialog">
				{!(isProcessingStep && importRun?.status !== "failed") ? (
					<div
						className={`${classes.header} ${step === STEPS.MAP ? classes.headerMap : ""} ${
							step === STEPS.REVIEW ? classes.headerReview : ""
						}`.trim()}>
						<div className={classes.headerTop}>
							<div>
								<h2 className={classes.title} id="ehr-upload-title">
									{stepTitle}
								</h2>
								<p className={classes.stepLabel}>
									{isProcessingStep ? "Processing import" : `Step ${step} of 3`}
								</p>
							</div>
							<button
								aria-label="Close upload modal"
								className={classes.closeButton}
								disabled={isUploading || isPreviewLoading}
								onClick={handleCloseModal}
								type="button">
								<X size={20} strokeWidth={2} />
							</button>
						</div>
						{step === STEPS.MAP ? (
							<p className={classes.mappingIntro}>
								Match your EHR column headers to TeleTrace fields. Required fields are marked.
							</p>
						) : null}
						{step === STEPS.REVIEW ? (
							<p className={classes.reviewIntro}>
								Review the details below before running. Validation cannot be undone once started.
							</p>
						) : null}
					</div>
				) : null}

				<div
					className={`${classes.body} ${step === STEPS.MAP ? classes.bodyMap : ""} ${
						step === STEPS.REVIEW ? classes.bodyReview : ""
					} ${isProcessingStep ? classes.bodyProcessing : ""}`.trim()}>
					<input
						accept=".csv,text/csv"
						className={classes.hiddenInput}
						onChange={handleInputChange}
						ref={fileInputRef}
						type="file"
					/>

					{step === STEPS.UPLOAD ? (
						selectedFile ? (
							<>
								<div className={classes.fileCard}>
									<div className={classes.fileIconWrap}>
										<FileSpreadsheet size={22} strokeWidth={2} />
									</div>
									<div className={classes.fileMeta}>
										<div className={classes.fileName}>{selectedFile.name}</div>
										<div className={classes.fileDetails}>
											{formatFileSize(selectedFile.size)}
											{rowCount ? ` · ${rowCount.toLocaleString("en-US")} rows detected` : ""}
										</div>
										{uploadStatus !== UPLOAD_STATUS.IDLE ? (
											<div className={getUploadStatusClass(uploadStatus)}>{uploadStatus}</div>
										) : null}
									</div>
									<button
										aria-label="Remove selected file"
										className={classes.removeButton}
										disabled={isUploading || isPreviewLoading || isValidating}
										onClick={handleRemoveFile}
										type="button">
										<X size={18} strokeWidth={2} />
									</button>
								</div>
								{validationError ? <p className={classes.errorText}>{validationError}</p> : null}
							</>
						) : (
							<div
								className={`${classes.dropZone} ${isDragActive ? classes.dropZoneActive : ""} ${
									isValidating ? classes.dropZoneDisabled : ""
								}`.trim()}
								onDragEnter={handleDragEnter}
								onDragLeave={handleDragLeave}
								onDragOver={handleDragOver}
								onDrop={handleDrop}
								onClick={handleBrowseClick}
								onKeyDown={(event) => {
									if (event.key === "Enter" || event.key === " ") {
										event.preventDefault();
										handleBrowseClick();
									}
								}}
								role="button"
								tabIndex={0}>
								<div className={classes.uploadIconWrap}>
									<UploadCloud size={22} strokeWidth={2} />
								</div>
								<p className={classes.dropTitle}>Drag and drop your CSV file here</p>
								<button
									className={classes.browseLink}
									onClick={(event) => {
										event.stopPropagation();
										handleBrowseClick();
									}}
									type="button">
									or click to browse
								</button>
								<p className={classes.dropHint}>Accepts .csv files only · Max 10MB</p>
							</div>
						)
					) : null}

					{step === STEPS.MAP ? (
						<div className={classes.mappingTableWrap}>
							<table className={classes.mappingTable}>
								<thead>
									<tr>
										<th scope="col">TeleTrace Field</th>
										<th scope="col">Your CSV Column</th>
									</tr>
								</thead>
								<tbody>
									{EHR_MAPPING_FIELDS.map(({ key, label, required }) => {
										const fieldError = mappingValidation.errors[key];

										return (
											<tr key={key}>
												<td className={classes.mappingFieldCell}>
													<span className={classes.mappingFieldLabel}>
														{label}
														{required ? (
															<span aria-hidden="true" className={classes.requiredMark}>
																*
															</span>
														) : null}
													</span>
												</td>
												<td className={classes.mappingSelectCell}>
													<div className={classes.selectWrap}>
														<select
															aria-invalid={Boolean(fieldError)}
															aria-label={`Map ${label}`}
															className={`${classes.mappingSelect} ${
																fieldError ? classes.mappingSelectError : ""
															}`.trim()}
															onChange={(event) => handleMappingChange(key, event.target.value)}
															value={columnMapping[key] || ""}>
															<option value="">Select column...</option>
															{csvHeaders.map((header) => {
																const isUsedElsewhere =
																	usedColumns.has(header.raw) && columnMapping[key] !== header.raw;

																return (
																	<option
																		disabled={isUsedElsewhere}
																		key={header.raw}
																		value={header.raw}>
																		{header.label}
																	</option>
																);
															})}
														</select>
														<ChevronDown aria-hidden="true" className={classes.selectIcon} size={16} />
													</div>
													{fieldError ? (
														<p className={classes.mappingFieldError}>{fieldError}</p>
													) : null}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					) : null}

					{step === STEPS.REVIEW ? (
						isPreviewLoading ? (
							<div className={classes.previewLoading}>
								<Spinner animation="border" size="sm" style={{ color: "#064260" }} />
								<p>Preparing import review...</p>
							</div>
						) : importPreview ? (
							<EhrCsvReviewStep importPreview={importPreview} uploadedByName={uploadedByName} />
						) : null
					) : null}

					{isProcessingStep && importRun ? <EhrCsvProcessingStep importRun={importRun} /> : null}
				</div>

				{isProcessingStep && importRun?.status === "failed" ? (
					<div className={classes.footer}>
						<button className={classes.nextButton} onClick={handleCloseModal} type="button">
							Close
						</button>
					</div>
				) : isProcessingStep ? null : step !== STEPS.REVIEW ? (
					<div className={classes.footer}>
						{step === STEPS.MAP ? (
							<button
								className={classes.cancelButton}
								disabled={isUploading || isPreviewLoading}
								onClick={handleBack}
								type="button">
								Back
							</button>
						) : (
							<button
								className={classes.cancelButton}
								disabled={isUploading || isPreviewLoading}
								onClick={handleCloseModal}
								type="button">
								Cancel
							</button>
						)}

						{step === STEPS.MAP ? (
							<button
								className={classes.nextButton}
								disabled={!canContinueToReview}
								onClick={handleContinueToReview}
								type="button">
								{isPreviewLoading ? "Loading..." : "Run Validation"}
							</button>
						) : (
							<button
								className={classes.nextButton}
								disabled={!canProceedToMapping}
								onClick={handleNext}
								type="button">
								Next
							</button>
						)}
					</div>
				) : (
					<div className={classes.footer}>
						<button
							className={classes.cancelButton}
							disabled={isUploading || isPreviewLoading}
							onClick={handleBack}
							type="button">
							<ArrowLeft aria-hidden="true" size={16} strokeWidth={2} />
							Back
						</button>
						<button
							aria-describedby={
								!canImportAndValidate && importPreview?.counts?.totalRows
									? "ehr-review-import-help"
									: undefined
							}
							className={classes.nextButton}
							disabled={!canImportAndValidate}
							onClick={handleImportAndValidate}
							title={
								!canImportAndValidate
									? "No records are ready to import. Check the warnings above."
									: undefined
							}
							type="button">
							{isUploading ? "Running..." : "Run Validation"}
						</button>
						{!canImportAndValidate && importPreview?.counts?.totalRows ? (
							<span className={classes.visuallyHidden} id="ehr-review-import-help">
								Run Validation is disabled because no records are ready to import.
							</span>
						) : null}
					</div>
				)}
			</div>
		</div>
	);
};

export default EhrCsvUploadModal;
